import React, { useState, useEffect } from 'react';
import { getTechniciens, createTechnicien, updateTechnicien, deleteTechnicien, resetPassword } from '../services/api';
import axios from 'axios';

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ fontSize: 18, padding: 4 }}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

const EMPTY_FORM = { login: '', password: '', nom: '', prenom: '', zoho_nom: '', secteur: '', telephone: '', entreprise: '', email: '', domicile_cp: '', domicile_ville: '' };

export default function Techniciens() {
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'create' | 'edit' | 'reset' | 'delete'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [resetPwd, setResetPwd] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const data = await getTechniciens();
      setTechs(data.techniciens || []);
    } catch {
      setTechs([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setError(''); setModal('create'); };
  const openEdit = t => { setSelected(t); setForm({ login: t.login, password: '', nom: t.nom, prenom: t.prenom, zoho_nom: t.zoho_nom, secteur: t.secteur || '', telephone: t.telephone || '', entreprise: t.entreprise || '', email: t.email || '', domicile_cp: t.domicile_cp || '', domicile_ville: t.domicile_ville || '' }); setError(''); setModal('edit'); };
  const openReset = t => { setSelected(t); setResetPwd(''); setError(''); setModal('reset'); };
  const openDelete = t => { setSelected(t); setModal('delete'); };

  const handleCreate = async () => {
    setSaving(true); setError('');
    try {
      await createTechnicien(form);
      setMsg('Technicien créé avec succès'); setModal(null); load();
    } catch(e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true); setError('');
    try {
      const r = await updateTechnicien(selected.id, { nom: form.nom, prenom: form.prenom, zoho_nom: form.zoho_nom, secteur: form.secteur, telephone: form.telephone, entreprise: form.entreprise, email: form.email, domicile_cp: form.domicile_cp, domicile_ville: form.domicile_ville });
      // Une commune que la Base Adresse Nationale ne reconnaît pas doit se
      // voir maintenant : sinon le domicile est enregistré mais le moteur ne
      // saura jamais le placer sur une carte.
      setMsg(r && r.domicile_geocode === false
        ? 'Technicien mis à jour — mais la commune du domicile est introuvable, vérifie l’orthographe'
        : 'Technicien mis à jour');
      setModal(null); load();
    } catch(e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!resetPwd || resetPwd.length < 6) { setError('Minimum 6 caractères'); return; }
    setSaving(true); setError('');
    try {
      await resetPassword(selected.id, resetPwd);
      setMsg('Mot de passe réinitialisé'); setModal(null);
    } catch(e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteTechnicien(selected.id);
      setMsg('Technicien supprimé'); setModal(null); load();
    } catch(e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const filtered = techs.filter(t =>
    `${t.nom} ${t.prenom} ${t.login} ${t.secteur} ${t.zoho_nom} ${t.telephone || ''} ${t.entreprise || ''} ${t.email || ''} ${t.domicile_cp || ''} ${t.domicile_ville || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggleVoirPrix = async (t) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`/api/admin/techniciens/${t.id}`, {
        nom: t.nom, prenom: t.prenom, zoho_nom: t.zoho_nom, secteur: t.secteur || '',
        voir_prix_devis: !t.voir_prix_devis,
      }, { headers: { Authorization: `Bearer ${token}` } });
      load();
    } catch { setMsg('Erreur lors de la mise à jour'); }
  };

  const toggleDisabled = async (t) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`/api/admin/techniciens/${t.id}`, {
        nom: t.nom, prenom: t.prenom, zoho_nom: t.zoho_nom, secteur: t.secteur || '',
        disabled: !t.disabled,
      }, { headers: { Authorization: `Bearer ${token}` } });
      load();
    } catch { setMsg('Erreur lors de la mise à jour'); }
  };

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Techniciens</div>
        <div className="topbar-actions">
          <input className="form-input" style={{ width: 220 }} placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={openCreate}>+ Nouveau technicien</button>
        </div>
      </div>
      <div className="page">
        {msg && <div className="alert alert-success" onClick={() => setMsg('')}>{msg} ✕</div>}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Technicien</th>
                  <th>Login</th>
                  <th>Téléphone</th>
                  <th>Email</th>
                  <th>Entreprise</th>
                  <th>Zoho OT</th>
                  <th>Secteur</th>
                  <th>Domicile</th>
                  <th>Créé le</th>
                  <th title="Autorisation à voir le prix du devis">Prix devis</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Chargement…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Aucun technicien</td></tr>
                ) : filtered.map(t => (
                  <tr key={t.id} style={t.disabled ? { background: 'rgba(239,68,68,0.07)', opacity: 0.8 } : undefined}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar" style={{ width: 32, height: 32, fontSize: 11, flexShrink: 0, background: t.disabled ? '#ef4444' : undefined }}>{t.prenom?.[0]}{t.nom?.[0]}</div>
                        <div>
                          <div style={{ fontWeight: 600, color: t.disabled ? '#ef4444' : undefined, textDecoration: t.disabled ? 'line-through' : undefined }}>{t.prenom} {t.nom}</div>
                          {t.disabled && <div style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', marginTop: 1 }}>DÉSACTIVÉ</div>}
                        </div>
                      </div>
                    </td>
                    <td><span className="mono">{t.login}</span></td>
                    <td><span style={{ color: 'var(--text2)', fontSize: 13 }}>{t.telephone || '—'}</span></td>
                    <td><span style={{ color: 'var(--text2)', fontSize: 13 }}>{t.email || '—'}</span></td>
                    <td><span style={{ color: 'var(--text2)', fontSize: 13 }}>{t.entreprise || '—'}</span></td>
                    <td><span className="tag">{t.zoho_nom}</span></td>
                    <td><span style={{ color: 'var(--text2)', fontSize: 13 }}>{t.secteur || '—'}</span></td>
                    <td><span style={{ color: 'var(--text2)', fontSize: 13 }}>{t.domicile_cp ? `${t.domicile_cp} ${t.domicile_ville || ''}`.trim() : '—'}</span></td>
                    <td><span style={{ color: 'var(--text3)', fontSize: 12 }}>{t.created_at ? new Date(t.created_at).toLocaleDateString('fr-FR') : '—'}</span></td>
                    <td>
                      <button onClick={() => toggleVoirPrix(t)} title={t.voir_prix_devis ? 'Désactiver' : 'Activer'} style={{ background: t.voir_prix_devis ? '#FCD34D' : 'var(--bg3)', border: `1px solid ${t.voir_prix_devis ? '#D97706' : 'var(--border)'}`, borderRadius: 8, padding: '3px 10px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: t.voir_prix_devis ? '#7C3E00' : 'var(--text3)' }}>
                        {t.voir_prix_devis ? '💰 Oui' : '—'}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => toggleDisabled(t)}
                          title={t.disabled ? 'Réactiver le technicien' : 'Désactiver le technicien'}
                          style={{
                            background: t.disabled ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.1)',
                            border: `1px solid ${t.disabled ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.35)'}`,
                            color: t.disabled ? '#16a34a' : '#ef4444',
                            fontWeight: 700,
                          }}
                        >
                          {t.disabled ? '✓ Réactiver' : '⊘ Désactiver'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(t)}>Modifier</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openReset(t)}>🔑 MDP</button>
                        <button className="btn btn-danger btn-sm" onClick={() => openDelete(t)}>Suppr.</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal créer */}
        {modal === 'create' && (
          <Modal title="Nouveau technicien" onClose={() => setModal(null)} footer={<>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? '...' : 'Créer'}</button>
          </>}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-row">
              <div className="form-group"><label className="form-label">Prénom</label><input className="form-input" value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Nom</label><input className="form-input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Login</label><input className="form-input" value={form.login} onChange={e => setForm(f => ({ ...f, login: e.target.value }))} placeholder="jean.dupont" /></div>
              <div className="form-group"><label className="form-label">Mot de passe</label><input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Téléphone WhatsApp</label>
                <input className="form-input" value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} placeholder="+33612345678" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jean.dupont@email.com" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Entreprise</label>
              <input className="form-input" value={form.entreprise} onChange={e => setForm(f => ({ ...f, entreprise: e.target.value }))} placeholder="Ex: Fibre Connect SAS" />
            </div>
            <div className="form-group">
              <label className="form-label">Valeur Technicien OT dans Zoho CRM</label>
              <input className="form-input" value={form.zoho_nom} onChange={e => setForm(f => ({ ...f, zoho_nom: e.target.value }))} placeholder="Ex: VALEA-D3_1" />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Doit correspondre exactement au champ Technicien_OT dans Zoho</div>
            </div>
            <div className="form-group"><label className="form-label">Secteur</label><input className="form-input" value={form.secteur} onChange={e => setForm(f => ({ ...f, secteur: e.target.value }))} placeholder="Ex: Île-de-France" /></div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Code postal domicile</label>
                <input className="form-input" value={form.domicile_cp} onChange={e => setForm(f => ({ ...f, domicile_cp: e.target.value }))} placeholder="Ex: 93200" />
              </div>
              <div className="form-group">
                <label className="form-label">Commune domicile</label>
                <input className="form-input" value={form.domicile_ville} onChange={e => setForm(f => ({ ...f, domicile_ville: e.target.value }))} placeholder="Ex: SAINT DENIS" />
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: -4, marginBottom: 12 }}>
              D’où il part le matin. Sert à proposer un rendez-vous sur une journée vide — sans domicile, le moteur n’a aucun critère de distance ce jour-là — et à compter ses kilomètres.
            </div>
          </Modal>
        )}

        {/* Modal modifier */}
        {modal === 'edit' && (
          <Modal title={`Modifier — ${selected?.prenom} ${selected?.nom}`} onClose={() => setModal(null)} footer={<>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? '...' : 'Enregistrer'}</button>
          </>}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-row">
              <div className="form-group"><label className="form-label">Prénom</label><input className="form-input" value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Nom</label><input className="form-input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} /></div>
            </div>
            <div className="form-group">
              <label className="form-label">Valeur Technicien OT dans Zoho CRM</label>
              <input className="form-input" value={form.zoho_nom} onChange={e => setForm(f => ({ ...f, zoho_nom: e.target.value }))} />
            </div>
            <div className="form-group"><label className="form-label">Secteur</label><input className="form-input" value={form.secteur} onChange={e => setForm(f => ({ ...f, secteur: e.target.value }))} /></div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Code postal domicile</label>
                <input className="form-input" value={form.domicile_cp} onChange={e => setForm(f => ({ ...f, domicile_cp: e.target.value }))} placeholder="Ex: 93200" />
              </div>
              <div className="form-group">
                <label className="form-label">Commune domicile</label>
                <input className="form-input" value={form.domicile_ville} onChange={e => setForm(f => ({ ...f, domicile_ville: e.target.value }))} placeholder="Ex: SAINT DENIS" />
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: -4, marginBottom: 12 }}>
              D’où il part le matin. Sert à proposer un rendez-vous sur une journée vide — sans domicile, le moteur n’a aucun critère de distance ce jour-là — et à compter ses kilomètres.
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Téléphone WhatsApp</label>
                <input className="form-input" value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} placeholder="+33612345678" />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Format international sans espaces</div>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jean.dupont@email.com" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Entreprise</label>
              <input className="form-input" value={form.entreprise} onChange={e => setForm(f => ({ ...f, entreprise: e.target.value }))} placeholder="Ex: Fibre Connect SAS" />
            </div>
          </Modal>
        )}

        {/* Modal reset MDP */}
        {modal === 'reset' && (
          <Modal title={`Réinitialiser MDP — ${selected?.prenom} ${selected?.nom}`} onClose={() => setModal(null)} footer={<>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleReset} disabled={saving}>{saving ? '...' : 'Réinitialiser'}</button>
          </>}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">Nouveau mot de passe</label>
              <input className="form-input" type="password" value={resetPwd} onChange={e => setResetPwd(e.target.value)} placeholder="Minimum 6 caractères" autoFocus />
            </div>
          </Modal>
        )}

        {/* Modal supprimer */}
        {modal === 'delete' && (
          <Modal title="Confirmer la suppression" onClose={() => setModal(null)} footer={<>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Annuler</button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={saving}>{saving ? '...' : 'Supprimer'}</button>
          </>}>
            <p style={{ color: 'var(--text2)' }}>Supprimer le technicien <strong style={{ color: 'var(--text)' }}>{selected?.prenom} {selected?.nom}</strong> ?</p>
            <p style={{ color: 'var(--text3)', fontSize: 12, marginTop: 8 }}>Cette action est irréversible. Les comptes-rendus existants ne seront pas supprimés.</p>
          </Modal>
        )}
      </div>
    </>
  );
}
